import { memo } from "react";
import { classNames } from "helpers";
import { ReactSVG } from "react-svg";
import { CHANNEL_CONFIG, DEFAULT_CHANNEL_CONFIG } from "services/api/sales/listings/consts";
import { SalesChannel } from "services/api/sales/channels/types";
import { ChannelPickerStepProps } from "./types";

const ChannelCard: React.FC<{
  channel: SalesChannel;
  selected: boolean;
  disabled: boolean;
  onToggle: () => void;
}> = ({ channel, selected, disabled, onToggle }) => {
  const cfg = CHANNEL_CONFIG[channel.code] || DEFAULT_CHANNEL_CONFIG;

  const handleClick = () => {
    if (disabled) return;
    onToggle();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={classNames(
        "relative flex items-center gap-3 p-4 rounded-sm border transition-all text-left",
        selected
          ? "border-sky-500 bg-sky-50"
          : "border-slate-200 bg-white hover:border-slate-300",
        disabled ? "cursor-not-allowed opacity-95" : "cursor-pointer"
      )}
    >
      <span
        className={classNames(
          "flex items-center justify-center w-10 h-10 rounded-sm flex-shrink-0",
          cfg.badge.bg,
          cfg.badge.text
        )}
      >
        <ReactSVG src={cfg.icon} className="w-5 h-5 [&_svg]:w-5 [&_svg]:h-5" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-800">{channel.name}</div>
        {disabled && selected && (
          <div className="text-xs text-slate-500 mt-0.5">Obavezan kanal</div>
        )}
      </div>
      <span
        className={classNames(
          "w-5 h-5 rounded border flex items-center justify-center flex-shrink-0",
          selected ? "bg-sky-600 border-sky-600 text-white" : "border-slate-300 bg-white"
        )}
      >
        {selected && (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </span>
    </button>
  );
};

const ChannelPickerStep: React.FC<ChannelPickerStepProps> = (props) => {
  const { channels, selectedChannelIds, marketplaceChannelId, onToggle } = props;

  // Nude se samo kanali čiju je integraciju tenant instalirao; vlastiti kanali
  // (marketplace, web stranica) nemaju integraciju pa su uvijek tu.
  const availableChannels = channels.filter((channel) => channel.isAvailable !== false);
  const hiddenCount = channels.length - availableChannels.length;

  return (
    <div className="w-full max-w-3xl mx-auto text-center">
      <h2 className="text-2xl font-semibold text-slate-800 mb-2">
        Na koje oglasnike želite objaviti vozilo?
      </h2>
      <p className="text-sm text-slate-500 mb-8">
        Odaberite oglasnike. Zajedničke podatke unosite jednom, kasnije ih možete prilagoditi za svaki oglasnik posebno.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-left">
        {availableChannels.map((channel) => (
          <ChannelCard
            key={channel.id}
            channel={channel}
            selected={selectedChannelIds.includes(channel.id)}
            disabled={channel.id === marketplaceChannelId}
            onToggle={() => onToggle(channel.id)}
          />
        ))}
      </div>

      {hiddenCount > 0 && (
        <p className="text-xs text-slate-400 mt-4">
          Ostali oglasnici ({hiddenCount}) pojavit će se ovdje kad im u Integracijama instalirate
          integraciju.
        </p>
      )}
    </div>
  );
};

export default memo(ChannelPickerStep);
